import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, LineElement, PointElement, LinearScale, TimeScale, Tooltip, Legend } from "chart.js";
import "chartjs-adapter-date-fns";
import { chartColors } from "../../config/theme";
import { useTheme } from "../../contexts/ThemeContext";

ChartJS.register(LineElement, PointElement, LinearScale, TimeScale, Tooltip, Legend);

// The realtime timeline chart, shared by the project dashboard (one line per
// area) and the day report (totals, comparison days dashed, international on a
// second axis). One set of axis, tooltip and theme rules for both.

export interface TimelineSeries {
  label: string;
  color: string;
  data: { x: number; y: number | null }[];
  dashed?: boolean;
  /** Plot against the right-hand axis (e.g. international beside people). */
  right?: boolean;
  width?: number;
}

const AreaTimelineChart: React.FC<{
  series: TimelineSeries[];
  rightAxisLabel?: string;
  leftAxisLabel?: string;
  /** Hour-of-day axis for a single day, instead of dated ticks. */
  hourAxis?: boolean;
  /** Off for printing: the printed chart must be its final state. */
  animate?: boolean;
  empty?: React.ReactNode;
}> = ({ series, rightAxisLabel, leftAxisLabel, hourAxis = false, animate = true, empty }) => {
  const { isDark } = useTheme();
  const hasRight = series.some((s) => s.right);

  const data = useMemo(
    () => ({
      datasets: series.map((s) => ({
        label: s.label,
        data: s.data,
        borderColor: s.color,
        backgroundColor: s.color,
        borderDash: s.dashed ? [6, 4] : undefined,
        yAxisID: s.right ? "y1" : "y",
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: s.width ?? 2,
        spanGaps: false,
      })),
    }),
    [series]
  );

  const options = useMemo(() => {
    const tick = { font: { size: 11 }, callback: (v: any) => (typeof v === "number" ? v.toLocaleString() : v) };
    const axisTitle = (text?: string) => (text ? { display: true, text, font: { size: 10 } } : undefined);
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: animate ? undefined : (false as const),
      plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
        tooltip: {
          callbacks: {
            label: (item: any) => ` ${item.dataset.label}: ${item.parsed.y.toLocaleString()}`,
          },
        },
      },
      scales: {
        x: {
          type: "time",
          time: hourAxis
            ? { unit: "hour", tooltipFormat: "HH:mm", displayFormats: { hour: "HH:mm" } }
            : {
                tooltipFormat: "EEE d MMM yyyy, HH:mm",
                displayFormats: { minute: "HH:mm", hour: "d MMM HH:mm", day: "d MMM", week: "d MMM", month: "MMM yyyy" },
              },
          ticks: { maxTicksLimit: hourAxis ? 12 : 7, font: { size: 11 } },
          grid: { display: false },
        },
        y: { beginAtZero: hourAxis, title: axisTitle(leftAxisLabel), ticks: tick, grid: { color: chartColors(isDark).grid } },
        ...(hasRight
          ? { y1: { position: "right", beginAtZero: hourAxis, title: axisTitle(rightAxisLabel), ticks: tick, grid: { display: false } } }
          : {}),
      },
      interaction: { mode: "nearest", intersect: false },
    } as any;
  }, [isDark, hasRight, hourAxis, animate, leftAxisLabel, rightAxisLabel]);

  if (!series.some((s) => s.data.some((p) => p.y != null)))
    return (
      <div className="flex h-full items-center justify-center text-sm text-ink-faint">
        {empty ?? "No realtime data for these areas yet."}
      </div>
    );
  return <Line data={data} options={options} />;
};

export default AreaTimelineChart;
